import { useMemo } from 'react';
import { Typography } from '@mui/material';

import Number from 'src/components/common/Number';
import Tooltip from 'src/components/common/Tooltip';
import GradientButton from 'src/components/GradientButton';
import IconButton from 'src/components/IconButton';
import { DYDX_TOKEN } from 'src/constants/tokens';
import type { Coin } from 'src/types/coin';
import { gt } from 'src/utils/number';
import { isMnemonicContain } from 'src/utils/string';

import {
  Container,
  ContentsContainer,
  DescriptionContainer,
  Divider,
  Header,
  HeaderTitle,
  StyledBottomSheet,
  TxInfoContainer,
  TxInfoItemColumnContainer,
  TxInfoItemContainer,
  TxInfoItemLeftContainer,
  TxInfoItemRightAmountColumnContainer,
  TxInfoItemRightAmountContainer,
  TxInfoItemRightContainer,
} from './styled';

import { ReactComponent as CloseIcon } from 'src/assets/svg/Close.svg';

type SignConfirmBottomSheetProps = Omit<React.ComponentProps<typeof StyledBottomSheet>, 'children'> & {
  title: string;
  descriptionText: string;
  displayEstimatedGasFeeAmount: string;
  amount: string;
  currentCoin?: Coin;
  recipientAddress: string;
  isButtonDisabled: boolean;
  isEstimation?: boolean;
  currentMemo?: string;
  onClickConfirm?: () => void;
};

export default function SignConfirmBottomSheet({
  descriptionText,
  title,
  displayEstimatedGasFeeAmount,
  recipientAddress,
  amount,
  currentCoin,
  isButtonDisabled,
  isEstimation = false,
  currentMemo,
  onClose,
  onClickConfirm,
  ...remainder
}: SignConfirmBottomSheetProps) {
  const gasFee = useMemo(() => String(parseFloat(displayEstimatedGasFeeAmount)), [displayEstimatedGasFeeAmount]);

  const errorMessage = useMemo(() => {
    if (currentMemo && isMnemonicContain(currentMemo)) {
      return 'Memo contains mnemonic';
    }
    if (isEstimation) {
      return 'Please wait for the transaction to be estimated.';
    }
    return '';
  }, [currentMemo, isEstimation]);

  return (
    <StyledBottomSheet {...remainder} onClose={onClose}>
      <Container>
        <ContentsContainer>
          <Header>
            <HeaderTitle>
              <Typography variant="h2">{title}</Typography>
            </HeaderTitle>
            <IconButton
              onClick={() => {
                onClose?.({}, 'backdropClick');
              }}
            >
              <CloseIcon />
            </IconButton>
          </Header>
          <DescriptionContainer>
            <Typography variant="h15">{descriptionText}</Typography>
          </DescriptionContainer>

          <TxInfoContainer>
            <TxInfoItemColumnContainer>
              <TxInfoItemLeftContainer>
                <Typography variant="h7">Recipient Address</Typography>
              </TxInfoItemLeftContainer>
              <TxInfoItemRightContainer>
                <Typography variant="h8">{recipientAddress}</Typography>
              </TxInfoItemRightContainer>
            </TxInfoItemColumnContainer>

            <TxInfoItemContainer>
              <TxInfoItemLeftContainer>
                <Typography variant="h7">Amount</Typography>
              </TxInfoItemLeftContainer>
              <TxInfoItemRightAmountColumnContainer>
                <TxInfoItemRightAmountContainer>
                  <Number typoOfIntegers="h4" typoOfDecimals="h7">
                    {amount}
                  </Number>
                  &nbsp;
                  <Typography variant="h6">{currentCoin?.symbol || ''}</Typography>
                </TxInfoItemRightAmountContainer>
              </TxInfoItemRightAmountColumnContainer>
            </TxInfoItemContainer>

            <Divider />

            <TxInfoItemContainer>
              <TxInfoItemLeftContainer>
                <Typography variant="h7">Estimated Gas Fee</Typography>
              </TxInfoItemLeftContainer>
              <TxInfoItemRightAmountContainer>
                <Number typoOfIntegers="h4" typoOfDecimals="h7">
                  {gt(gasFee, '0') ? gasFee : '0'}
                </Number>
                &nbsp;
                <Typography variant="h6">{DYDX_TOKEN.symbol}</Typography>
              </TxInfoItemRightAmountContainer>
            </TxInfoItemContainer>

            {currentMemo && (
              <TxInfoItemColumnContainer>
                <TxInfoItemLeftContainer>
                  <Typography variant="h7">Memo</Typography>
                </TxInfoItemLeftContainer>
                <Typography variant="h7">{currentMemo}</Typography>
              </TxInfoItemColumnContainer>
            )}
          </TxInfoContainer>
        </ContentsContainer>
        <Tooltip varient="error" title={errorMessage} placement="top" arrow>
          <div>
            <GradientButton
              disabled={!!errorMessage || isButtonDisabled}
              onClick={() => {
                onClickConfirm?.();
              }}
            >
              <Typography variant="h4">CONFIRM</Typography>
            </GradientButton>
          </div>
        </Tooltip>
      </Container>
    </StyledBottomSheet>
  );
}
